import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { api, ApiError, type MeResponse } from "@/lib/api";

/**
 * Session state for the admin console.
 *
 * Rank comparison is duplicated from the server on purpose — it drives which controls are
 * *shown*. Authorisation itself is enforced server-side on every route; hiding a button is
 * a courtesy, not a permission.
 */

const RANK: Record<string, number> = { owner: 40, manager: 30, bartender: 20, viewer: 10 };

export const useAuthStore = defineStore("auth", () => {
  const user = ref<NonNullable<MeResponse["user"]> | null>(null);
  const checked = ref(false);
  const busy = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);

  function atLeast(role: keyof typeof RANK): boolean {
    const current = user.value?.role;
    if (!current) return false;
    return (RANK[current] ?? 0) >= (RANK[role] ?? 99);
  }

  async function refresh(): Promise<void> {
    try {
      const me = await api.get<MeResponse>("/api/auth/me");
      user.value = me.authenticated ? (me.user ?? null) : null;
    } catch {
      user.value = null;
    } finally {
      checked.value = true;
    }
  }

  /** Request a sign-in link. Never reveals whether the address is known. */
  async function requestLink(email: string, turnstileToken?: string): Promise<boolean> {
    busy.value = true;
    error.value = null;
    try {
      await api.post("/api/auth/request-link", { email, turnstileToken });
      return true;
    } catch (e) {
      error.value =
        e instanceof ApiError && e.status === 429
          ? "Too many attempts. Try again in 30 minutes."
          : "Could not send a sign-in link.";
      return false;
    } finally {
      busy.value = false;
    }
  }

  async function verifyToken(token: string): Promise<boolean> {
    busy.value = true;
    error.value = null;
    try {
      await api.post("/api/auth/verify", { token });
      await refresh();
      return isAuthenticated.value;
    } catch {
      error.value = "That sign-in link is invalid or has expired.";
      return false;
    } finally {
      busy.value = false;
    }
  }

  async function verifyCode(email: string, code: string): Promise<boolean> {
    busy.value = true;
    error.value = null;
    try {
      await api.post("/api/auth/verify-code", { email, code: code.toUpperCase() });
      await refresh();
      return isAuthenticated.value;
    } catch {
      error.value = "That code is invalid or has expired.";
      return false;
    } finally {
      busy.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post("/api/auth/logout");
    } finally {
      user.value = null;
    }
  }

  return {
    user,
    checked,
    busy,
    error,
    isAuthenticated,
    atLeast,
    refresh,
    requestLink,
    verifyToken,
    verifyCode,
    logout,
  };
});
